<template>
  <ReachInput
      ref="input"
      :editor-mode="editorMode"
      :field-config="fieldConfig"
      :formField="validatedField"
      :grid-editor="gridEditor"
      :help-message="fieldConfig.labelOnly ? '' :fieldConfig.htmlHelpText"
      :help-title="fieldConfig.labelOnly ? '' : fieldConfig.helpWindowTitle"
      :hint="fieldConfig.labelOnly ? '' :fieldConfig.shortHelpText"
      :initialFocus="fieldConfig.autofocus"
      :label="fieldConfig.labelOnly ? '\u00A0' : fieldConfig.fieldLabel"
      :mode="mode"
      :open-from-grid="openFromGrid"
      :parent-form="parentForm"
      :read-only="readOnly || !validatedField || validatedField.readOnly"
      :read-only-text="readOnlyText"
  >
    <template v-if="orderNumberForNaoko && formIsProject" slot="right-slot">
      <b-button :disabled="readOnlyButton || lockedDuringProcessRpcRequest" class="input-group-text solid"
                style="height: 38px"
                @click="generateOrderNumber">
        <font-awesome-icon icon="refresh"/>
      </b-button>
    </template>

  </ReachInput>
</template>

<script>

import InputBase from "@/components/inputs/InputBase";
import ReachInput from "@/components/inputs/ReachInput";
import FormValidatedField from "@/components/forms/FormValidatedField";
import TextFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/TextFormFieldConfig";
import SelectActivityRenderer from "@/components/forms/renderers/SelectActivityRenderer.vue";
import dialogManager from "@/utils/DialogManager";
import Rpc from "@/Rpc";
import ProductInSeason from "@/server/com/silkplm/model/plm/ProductInSeason";


export default {
  name: "SkuNumberRenderer",
  components: {SelectActivityRenderer, InputBase, ReachInput},
  async created() {
  },

  async mounted() {
    this.orderNumberForNaoko = await Rpc.plm.services.FeatureFlagService.selectFlagIs("orderNumberPlugin", "Order Number for NAOKO");
    this.$nextTick(() => {
      this.afterMounted();
    });
  },

  props: {
    gridEditor: {default: null},
    selectedRows: {default: null},
    openFromGrid: {default: false},
    openFromMultiSelectGrid: {default: false},
    validatedField: {
      type: FormValidatedField, required: true
    },
    fieldConfig: {
      type: TextFormFieldConfig, required: true
    },
    parentForm: {
      default: null
    },
    mode: {
      default: null
    }
  },

  methods: {
    afterMounted() {
      this.handleChangeReadOnly();
    },

    async generateOrderNumber() {
      if (!this.openFromMultiSelectGrid) {
        await this.generateOrderNumberForNAOKO(this.validatedField.validatedForm.originalSource);
        return;
      }
      let selectedEntities = [];
      if (this.selectedRows?.materialize) {
        await this.selectedRows.materialize();
        for (let item of this.selectedRows) {
          selectedEntities.push(item);
        }
      }

      let projectsWithContinuation = [];

      selectedEntities.forEach(entity => {
        if (entity.continuation) {
          projectsWithContinuation.push(entity.numberOfProduct);
        }
      });

      if (projectsWithContinuation.length) {
        let projectsText = "";
        projectsWithContinuation.forEach(numberOfProduct => {
          projectsText += numberOfProduct + ", ";
        });
        if (projectsText.lastIndexOf(", ") !== -1) {
          let index = projectsText.lastIndexOf(",");
          projectsText = projectsText.substring(0, index) + projectsText.substring(index + 1);
        }
        let message = $tr("orderNumberRenderer.continuationWarning", "Projects with numbers: #{1} " +
            "has continuation - you can't assign Order Number.", projectsText);
        await dialogManager.info($tr("orderNumberRenderer.orderNumberWarning", "Can't assign Order Number"), message);
        return;
      }

      let projectsWithNotEmptyOrderNumber = [];
      selectedEntities.forEach(entity => {
        if (entity.orderNumber?.length) {
          projectsWithNotEmptyOrderNumber.push(entity.numberOfProduct);
        }
      });

      if (projectsWithNotEmptyOrderNumber.length) {
        let projectsText = "";
        projectsWithNotEmptyOrderNumber.forEach(numberOfProduct => {
          projectsText += numberOfProduct + ", ";
        });
        if (projectsText.lastIndexOf(", ") !== -1) {
          let index = projectsText.lastIndexOf(",");
          projectsText = projectsText.substring(0, index) + projectsText.substring(index + 1);
        }
        let message = $tr("orderNumberRenderer.notEmptyWarning", "Projects with numbers: #{1} " +
            "has already not empty values - you can't assign Order Number.", projectsText);
        await dialogManager.info($tr("orderNumberRenderer.orderNumberWarning", "Can't assign Order Number"), message);
        return;
      }

      this.bulkErrorMessage = "";
      for (const entity of selectedEntities) {
        await this.generateOrderNumberForNAOKO(entity);
      }
      if (!this.bulkErrorMessage?.length) {
        let res = new Map();
        for (const el of selectedEntities) {
          res[el.id] = el.orderNumber;
        }
        await Rpc.plm.services.ProductInSeasonService.bulkAssignOrderNumber(res);

      } else {
        await dialogManager.info($tr("orderNumberRenderer.orderNumberWarning", "Can't assign Order Number"), this.bulkErrorMessage);
      }
    },

    async generateOrderNumberForNAOKO(entity) {
      try {
        this.lockedDuringProcessRpcRequest = true;
        let orderNumber = await Rpc.plm.services.ProductInSeasonService.computeOrderNumberForProductInSeasonForNaokoPathAfterClickButton(
            entity.id,
            entity.category,
            entity.seasonId,
            entity.continuation
        ).lock();
        if (orderNumber?.length) {
          if (this.openFromGrid) {
            if (!this.openFromMultiSelectGrid) {
              entity.orderNumber = orderNumber;
              this.validatedField.validatedForm.validatedFieldByName("shouldUpgradeOrderNumberSequence").value = true;
              entity.shouldUpgradeOrderNumberSequence = true;
              await entity.save();
            }
          }
        }
        if (!orderNumber) {
          let warning = await Rpc.plm.services.ProductInSeasonService.computeOrderNumberForNaokoWarning(
              entity.numberOfProduct,
              entity.category,
              entity.seasonId,
          ).lock();
          if (warning && warning.length) {
            if (!this.openFromMultiSelectGrid) {
              await dialogManager.info($tr("orderNumberRenderer.orderNumberWarning", "Can't assign Order Number"), warning);
            } else {
              this.bulkErrorMessage += warning + " <br>";
            }
          }
        } else {
          if (!this.openFromMultiSelectGrid) {
            this.validatedField.value = orderNumber;
            this.validatedField.validatedForm.validatedFieldByName("shouldUpgradeOrderNumberSequence").value = true;
          }
        }
      } finally {
        this.lockedDuringProcessRpcRequest = false;
      }
    },

    async setReadOnly(readOnly) {
      this.readOnly = this.fieldConfig.readOnly || readOnly;
      this.handleChangeReadOnly();
    },

    setEditorMode(val) {
      if (this.SKURangeFlagEnabled || this.SKUOnlyText || this.SKUForNaoko) {
        this.editorMode = val;
      }
    },
    handleChangeReadOnly() {
      if (this.orderNumberForNaoko) {
        if (this.readOnly) {
          this.readOnlyButton = true;
          return;
        }
        if (this.validatedField?.validatedForm?.originalSource?.continuation && this.validatedField.value?.length) {
          this.readOnlyButton = true;
          this.readOnlyText = false;
        } else {
          if (this.validatedField?.validatedForm?.originalSource?.continuation) {
            this.readOnlyText = false;
          } else {
            this.readOnlyText = true;
          }
          if (!this.validatedField.value?.length && !this.validatedField?.validatedForm?.originalSource?.continuation) {
            this.readOnlyButton = false;
          } else {
            this.readOnlyButton = true;
          }
        }
      }
    }
  }
  ,

  watch: {
    "validatedField.value":

        function (val) {
          if (this.orderNumberForNaoko) {
            this.handleChangeReadOnly();
          }
        }

    ,
    readOnly: function (val) {
      this.handleChangeReadOnly();
    }
  }
  ,

  data: function () {
    return {
      formIsProject: this.validatedField.validatedForm.formConfig.formClassLong === ProductInSeason.getRemoteClass(),
      lockedDuringProcessRpcRequest: false,
      orderNumberForNaoko: false,
      readOnlyButton: true,
      readOnly: true,
      editorMode: false,
      form: this.validatedField.validatedForm.formConfig,
      masterFormModel: null,
      readOnlyText: false
    }
  }
}

</script>

<style scoped>

</style>